import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ConfidenceChip } from '@/components/ai-accountant/confidence-chip';
import { Banner, FilePane } from '@/components/ai-accountant/review-parts';
import { StatementReview } from '@/components/ai-accountant/statement-review';
import { AiStatusChip } from '@/components/ai-accountant/status-chip';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { AmountInput } from '@/components/amount-input';
import { Combobox, type ComboItem } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccounts, useTaxCodes, taxCodesFor } from '@/lib/accounts';
import {
  RECORD_AS_LABEL,
  initialForm,
  minorToDecimal,
  statementOf,
  unconfirmedLow,
  useAiDocument,
  type AiDocumentDetail,
  type Field as AiField,
  type RecordAs,
  type ReviewForm,
  type Voucher,
} from '@/lib/ai-accountant';
import { api } from '@/lib/api';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDateTime } from '@/lib/format';
import { GST_STATES } from '@/lib/gst';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { isIndia, useHomeCurrency } from '@/lib/org-locale';
import { documentPath, errorText, isClientRole, useOrgRole } from '@/lib/portal';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/ai-accountant/$id')({ component: Review });

/**
 * Screen AI2 (document 06): review one AI Accountant proposal, QBO Receipts "For review" style.
 * Left, the file and the text read from it; right, what to record with a confidence chip on each
 * suggested field, the line grid, totals and the voucher it will post. Approve and create records
 * it; nothing is recorded before.
 */
function Review() {
  const { id } = Route.useParams();
  const role = useOrgRole();
  const doc = useAiDocument(id);
  if (role && isClientRole(role))
    return <EmptyState title="Only accounting staff use the AI Accountant" />;
  if (doc.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (doc.isError)
    return <EmptyState title="Could not load the document" hint={doc.error.message} />;
  const d = doc.data;
  // a bank statement is reviewed transaction by transaction and sent to Banking (AI3)
  const statement = statementOf(d);
  if (statement)
    return <StatementReview key={`${d.id}:${d.version}:${d.status}`} d={d} p={statement} />;
  return <ReviewScreen key={`${d.id}:${d.version}:${d.status}`} d={d} />;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ReviewScreen({ d }: { d: AiDocumentDetail }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const home = useHomeCurrency();
  const p = d.proposal;
  const editable = d.status === 'for_review';
  const [form, setForm] = useState<ReviewForm>(() => initialForm(d, todayIso()));
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = (key: string) => setConfirmed((s) => new Set(s).add(key));
  const set = <K extends keyof ReviewForm>(k: K, v: ReviewForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i: number, patch: Partial<ReviewForm['lines'][number]>) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)) }));

  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const vendors = useContacts({ kind: 'vendor' });
  const india = isIndia();

  const lineAccounts: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter(
          (a) =>
            a.isActive &&
            !a.isControl &&
            !['bank', 'credit_card'].includes(a.accountType) &&
            a.detailType !== 'undeposited_funds',
        )
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${a.fullName}`,
          keywords: `${a.accountTypeName} ${a.detailTypeName}`,
          group: a.accountTypeName,
          hint: a.detailTypeName,
        })),
    [accounts.data],
  );
  const paymentAccounts = (accounts.data ?? []).filter(
    (a) => a.isActive && ['bank', 'savings', 'cash_on_hand', 'credit_card'].includes(a.detailType),
  );
  const creditAccounts: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isActive && !a.isControl)
        .map((a) => ({ id: a.id, label: `${a.code} ${a.fullName}`, group: a.accountTypeName })),
    [accounts.data],
  );
  const purchaseCodes = taxCodesFor(taxCodes.data, 'purchases').filter(
    (t) => !(india && t.gstSplit === 'igst'),
  );
  const vendorItems: ComboItem[] = useMemo(
    () =>
      (vendors.data ?? []).map((v) => ({
        id: v.id,
        label: v.displayName,
        hint: v.gstin ?? undefined,
        keywords: v.gstin ?? '',
      })),
    [vendors.data],
  );

  const ready =
    editable &&
    form.lines.length > 0 &&
    form.lines.every((l) => l.amount && Number.isFinite(Number(l.amount))) &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.issueDate);
  const debounced = useDebounced(form, 400);
  const preview = useQuery({
    queryKey: ['ai-accountant', orgId, 'preview', d.id, debounced],
    queryFn: () =>
      api<{ voucher: Voucher; subtotal: string; tax: string; total: string }>(
        `/orgs/${orgId}/ai-accountant/${d.id}/preview`,
        { method: 'POST', body: JSON.stringify(body(debounced)) },
      ),
    enabled: Boolean(orgId) && ready,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const after = () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] });
  const save = useMutation({
    mutationFn: () =>
      api<AiDocumentDetail>(`/orgs/${orgId}/ai-accountant/${d.id}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ ...body(form), version: d.version }),
      }),
    onSuccess: () => toast('Saved for later. Nothing has been recorded yet.'),
    onError: (e) => setError(errorText(e)),
    onSettled: after,
  });
  const approve = useMutation({
    mutationFn: () =>
      api<{ created: { type: RecordAs; id: string; number: string | null } }>(
        `/orgs/${orgId}/ai-accountant/${d.id}/approve`,
        { method: 'POST', body: JSON.stringify({ ...body(form), version: d.version }) },
      ),
    onSuccess: (r) => {
      const c = r.created;
      toast(`${RECORD_AS_LABEL[c.type]} ${c.number ?? ''} created from ${d.fileName}.`, {
        link: {
          to:
            c.type === 'journal_entry'
              ? `/accounting/journal-entries/${c.id}`
              : documentPath(c.type, c.id),
          label: 'View',
        },
      });
      void navigate({ to: '/ai-accountant', search: { status: 'for_review' } });
    },
    onError: (e) => setError(errorText(e)),
    onSettled: async () => {
      await after();
      await qc.invalidateQueries({ queryKey: ['portal', orgId] });
    },
  });
  const rerun = useMutation({
    mutationFn: () =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/rerun`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSuccess: () => toast('Sent to the AI Accountant again.'),
    onError: (e) => setError(errorText(e)),
    onSettled: after,
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason, version: d.version }),
      }),
    onSuccess: () => {
      setRejecting(false);
      toast(`${d.fileName} rejected.`);
    },
    onSettled: after,
  });
  const reopen = useMutation({
    mutationFn: () =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSettled: after,
  });

  const low = unconfirmedLow(p, form, confirmed);
  const voucher = preview.data?.voucher ?? null;
  const canApprove =
    editable && ready && low.length === 0 && Boolean(voucher?.balanced) && !preview.isFetching;
  const edited = (key: string, now: unknown, field: AiField<unknown> | undefined | null) =>
    Boolean(field) &&
    field!.value !== now &&
    !(field!.value === null && (now === null || now === ''));
  const chip = (
    key: string,
    field: AiField<unknown> | undefined | null,
    now: unknown,
    onPick?: (v: unknown) => void,
  ) =>
    p && field ? (
      <ConfidenceChip
        testId={`chip-${key}`}
        field={field}
        edited={edited(key, now, field)}
        confirmed={confirmed.has(key)}
        onConfirm={() => confirm(key)}
        {...(onPick && editable ? { onPick } : {})}
      />
    ) : null;
  const printedTotal = p?.totalMinor.value ? minorToDecimal(p.totalMinor.value) : null;
  const ccy = form.currency || home;

  return (
    <div className="pb-20">
      <Link
        to="/ai-accountant"
        search={{ status: 'for_review' }}
        className="mb-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> AI Accountant
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.7rem] font-medium tracking-[-0.01em]">
            {d.fileName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Uploaded {formatDateTime(d.uploadedAt)}
            {d.uploadedBy ? ` by ${d.uploadedBy}` : ''}
            {d.inbox
              ? ` · from the document inbox${d.inbox.uploadedBy ? ` (${d.inbox.uploadedBy})` : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <AiStatusChip status={d.status} />
          {p ? (
            <span
              data-testid="engine"
              className="inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs"
            >
              <Sparkles className="size-3 text-primary" aria-hidden />
              {p.engine === 'jev'
                ? `Suggested by TypeSafe Jev${d.model ? ` (${d.model})` : ''}`
                : 'Suggested by rules'}
            </span>
          ) : null}
        </div>
      </div>

      {p?.notice ? <Banner tone="warning">{p.notice}</Banner> : null}
      {d.status === 'processing' ? (
        <Banner tone="info">
          <Loader2 className="size-4 animate-spin" aria-hidden /> The AI Accountant is reading this
          document. This page updates when it is ready.
        </Banner>
      ) : null}
      {d.status === 'failed' ? (
        <Banner tone="error">
          Could not process this document{d.error ? `: ${d.error}` : '.'} Re-run it, or reject it
          and record it by hand.
        </Banner>
      ) : null}
      {d.status === 'rejected' ? (
        <Banner tone="error">
          Rejected{d.reviewedBy ? ` by ${d.reviewedBy}` : ''}: {d.rejectReason}
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => reopen.mutate()}>
            Back to review
          </Button>
        </Banner>
      ) : null}
      {d.status === 'recorded' ? (
        <Banner tone="success">
          <CheckCircle2 className="size-4" aria-hidden />
          Recorded{d.reviewedBy ? ` by ${d.reviewedBy}` : ''}
          {d.reviewedAt ? ` on ${formatDateTime(d.reviewedAt)}` : ''} as{' '}
          {d.document ? (
            <Link
              className="font-medium text-primary hover:underline"
              to={documentPath(d.document.kind, d.document.id)}
            >
              {d.document.kind === 'expense' ? 'Expense' : 'Bill'} {d.document.number ?? '(draft)'}
            </Link>
          ) : d.journalEntry ? (
            <Link
              className="font-medium text-primary hover:underline"
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: d.journalEntry.id }}
            >
              Journal entry {d.journalEntry.entryNo}
            </Link>
          ) : null}
          .
        </Banner>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <FilePane d={d} />

        <section className="space-y-4" aria-label="What to record">
          <ErrorBanner>{error}</ErrorBanner>
          {p?.checks.filter((c) => !c.ok).length ? (
            <ul
              className="space-y-1 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm"
              data-testid="checks"
            >
              {p.checks
                .filter((c) => !c.ok)
                .map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                    {c.message}
                  </li>
                ))}
            </ul>
          ) : null}

          <fieldset disabled={!editable} className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="text-sm font-medium">Record as</span>
                {chip('recordAs', p?.recordAs, form.recordAs, (v) =>
                  set('recordAs', v as RecordAs),
                )}
              </div>
              <div
                className="inline-flex rounded-md border"
                role="radiogroup"
                aria-label="Record as"
              >
                {(['bill', 'expense', 'journal_entry'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={form.recordAs === k}
                    disabled={!editable || (k === 'journal_entry' && Boolean(d.inbox))}
                    title={
                      k === 'journal_entry' && d.inbox
                        ? 'An upload from the document inbox is recorded as a bill or an expense'
                        : undefined
                    }
                    className={cn(
                      'px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md disabled:opacity-50',
                      form.recordAs === k
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-muted',
                    )}
                    onClick={() => set('recordAs', k)}
                  >
                    {RECORD_AS_LABEL[k]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <Labelled
                label="Vendor"
                chip={chip('vendor', p?.vendor, form.vendorId, (v) =>
                  setForm((f) => ({ ...f, vendorId: v as string, newVendor: null })),
                )}
              >
                {form.newVendor && !form.vendorId ? (
                  <div
                    className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-sm"
                    data-testid="new-vendor"
                  >
                    <div className="font-medium">New vendor: {form.newVendor.name}</div>
                    <Input
                      aria-label="New vendor name"
                      value={form.newVendor.name}
                      onChange={(e) =>
                        set('newVendor', { ...form.newVendor!, name: e.target.value })
                      }
                    />
                    {india ? (
                      <Input
                        aria-label="New vendor GSTIN"
                        placeholder="GSTIN (optional)"
                        value={form.newVendor.gstin ?? ''}
                        onChange={(e) =>
                          set('newVendor', {
                            ...form.newVendor!,
                            gstin: e.target.value.toUpperCase() || null,
                          })
                        }
                      />
                    ) : null}
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => set('newVendor', null)}
                    >
                      Choose an existing vendor instead
                    </button>
                  </div>
                ) : (
                  <Combobox
                    ariaLabel="Vendor"
                    items={vendorItems}
                    value={form.vendorId}
                    onChange={(v) => setForm((f) => ({ ...f, vendorId: v, newVendor: null }))}
                    placeholder="Choose a vendor"
                    onAddNew={(typed) =>
                      setForm((f) => ({
                        ...f,
                        vendorId: null,
                        newVendor: {
                          name: typed || p?.newVendor?.name || p?.vendorName.value || '',
                          gstin: p?.newVendor?.gstin ?? p?.supplierGstin.value ?? null,
                        },
                      }))
                    }
                    addNewLabel={
                      (p?.newVendor?.name ?? p?.vendorName.value)
                        ? `Add vendor ${p?.newVendor?.name ?? p?.vendorName.value}`
                        : 'Add new vendor'
                    }
                  />
                )}
              </Labelled>
              <Labelled label="Bill no." chip={chip('billNo', p?.billNo, form.billNo)}>
                <Input
                  aria-label="Bill no."
                  value={form.billNo ?? ''}
                  onChange={(e) => set('billNo', e.target.value || null)}
                />
              </Labelled>
              <Labelled
                label={form.recordAs === 'journal_entry' ? 'Journal date' : 'Bill date'}
                chip={chip('issueDate', p?.issueDate, form.issueDate)}
              >
                <DateInput
                  aria-label="Bill date"
                  value={form.issueDate}
                  onChange={(v) => set('issueDate', v)}
                />
              </Labelled>
              {form.recordAs === 'bill' ? (
                <Labelled label="Due date" chip={chip('dueDate', p?.dueDate, form.dueDate)}>
                  <DateInput
                    aria-label="Due date"
                    value={form.dueDate ?? ''}
                    onChange={(v) => set('dueDate', v || null)}
                  />
                </Labelled>
              ) : null}
              {india ? (
                <Labelled
                  label="Place of supply"
                  chip={chip('placeOfSupply', p?.placeOfSupply, form.placeOfSupply)}
                >
                  <NativeSelect
                    aria-label="Place of supply"
                    value={form.placeOfSupply ?? ''}
                    onChange={(e) => set('placeOfSupply', e.target.value || null)}
                  >
                    <option value="">—</option>
                    {GST_STATES.map(([code, name]) => (
                      <option key={code} value={code}>
                        {code}-{name}
                      </option>
                    ))}
                  </NativeSelect>
                </Labelled>
              ) : null}
              <Labelled label="Currency" chip={chip('currency', p?.currency, form.currency)}>
                <NativeSelect
                  aria-label="Currency"
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value)}
                >
                  {[...new Set([home, form.currency, p?.currency.value ?? home])].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </NativeSelect>
              </Labelled>
              {form.recordAs === 'expense' ? (
                <Labelled
                  label="Paid from"
                  chip={chip('paymentAccount', p?.paymentAccount, form.paymentAccountId)}
                >
                  <NativeSelect
                    aria-label="Paid from"
                    value={form.paymentAccountId ?? ''}
                    onChange={(e) => set('paymentAccountId', e.target.value || null)}
                  >
                    <option value="">Choose an account</option>
                    {paymentAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.fullName}
                      </option>
                    ))}
                  </NativeSelect>
                </Labelled>
              ) : null}
              {form.recordAs === 'journal_entry' ? (
                <Labelled label="Credit account">
                  <Combobox
                    ariaLabel="Credit account"
                    items={creditAccounts}
                    value={form.paymentAccountId}
                    onChange={(v) => set('paymentAccountId', v)}
                    placeholder="Account to credit"
                  />
                </Labelled>
              ) : (
                <Labelled label="Amounts are">
                  <NativeSelect
                    aria-label="Amounts are"
                    value={form.amountsAre}
                    onChange={(e) => set('amountsAre', e.target.value as ReviewForm['amountsAre'])}
                  >
                    <option value="exclusive">Exclusive of tax</option>
                    <option value="inclusive">Inclusive of tax</option>
                  </NativeSelect>
                </Labelled>
              )}
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table aria-label="Lines">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead className="min-w-[220px]">Account</TableHead>
                    <TableHead className="min-w-[160px]">Description</TableHead>
                    <TableHead className="w-[140px] text-right">Amount</TableHead>
                    {form.recordAs !== 'journal_entry' ? (
                      <TableHead className="w-[170px]">Tax code</TableHead>
                    ) : null}
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.lines.map((l, i) => {
                    const pl = p?.lines[i];
                    return (
                      <TableRow key={i} data-testid="review-line">
                        <TableCell className="tnum align-top text-muted-foreground">
                          {i + 1}
                        </TableCell>
                        <TableCell className="align-top">
                          <Combobox
                            ariaLabel={`Line ${i + 1} account`}
                            items={lineAccounts}
                            value={l.accountId}
                            onChange={(v) => setLine(i, { accountId: v })}
                            placeholder="Choose an account"
                            invalid={!l.accountId}
                          />
                          <div className="mt-1">
                            {chip(`lines.${i}.account`, pl?.account, l.accountId, (v) =>
                              setLine(i, { accountId: v as string }),
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                            aria-label={`Line ${i + 1} description`}
                            value={l.description}
                            onChange={(e) => setLine(i, { description: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="align-top">
                          <AmountInput
                            aria-label={`Line ${i + 1} amount`}
                            className="text-right"
                            value={l.amount}
                            onValueChange={(v) => setLine(i, { amount: v })}
                            invalid={!l.amount}
                          />
                        </TableCell>
                        {form.recordAs !== 'journal_entry' ? (
                          <TableCell className="align-top">
                            <NativeSelect
                              aria-label={`Line ${i + 1} tax code`}
                              value={l.taxCodeId ?? ''}
                              onChange={(e) => setLine(i, { taxCodeId: e.target.value || null })}
                            >
                              <option value="">No tax</option>
                              {purchaseCodes.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.code} {t.name}
                                </option>
                              ))}
                            </NativeSelect>
                            <div className="mt-1">
                              {chip(`lines.${i}.taxCode`, pl?.taxCode, l.taxCodeId, (v) =>
                                setLine(i, { taxCodeId: v as string }),
                              )}
                            </div>
                          </TableCell>
                        ) : null}
                        <TableCell className="align-top">
                          {form.lines.length > 1 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Delete line ${i + 1}`}
                              onClick={() =>
                                setForm((f) => ({ ...f, lines: f.lines.filter((_, n) => n !== i) }))
                              }
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {editable ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    lines: [
                      ...f.lines,
                      { description: '', amount: '', accountId: null, taxCodeId: null },
                    ],
                  }))
                }
              >
                <Plus className="size-4" /> Add line
              </Button>
            ) : null}
            {india && form.recordAs !== 'journal_entry' ? (
              <p className="text-xs text-muted-foreground">
                CGST and SGST, or IGST, follow from the vendor's state
                {p?.gstRegime
                  ? ` (${p.gstRegime === 'inter' ? 'IGST: another state' : 'CGST + SGST: same state'})`
                  : ''}
                .
              </p>
            ) : null}

            <div className="ml-auto w-full max-w-xs space-y-1 text-sm" data-testid="totals">
              <Row
                label="Subtotal"
                value={preview.data ? `${ccy} ${formatAmount(preview.data.subtotal)}` : '—'}
              />
              <Row
                label="Tax"
                value={preview.data ? `${ccy} ${formatAmount(preview.data.tax)}` : '—'}
              />
              <Row
                label="Total"
                strong
                value={preview.data ? `${ccy} ${formatAmount(preview.data.total)}` : '—'}
              />
              {printedTotal && preview.data && preview.data.total !== printedTotal ? (
                <p className="text-xs text-warning">
                  The document's total reads {ccy} {formatAmount(printedTotal)}.
                </p>
              ) : null}
            </div>
          </fieldset>

          <VoucherPreview
            voucher={voucher}
            pending={preview.isFetching}
            error={preview.error ? errorText(preview.error) : null}
          />
          {d.usage ? (
            <p className="text-xs text-muted-foreground">
              {d.usage.requests} TypeSafe request{d.usage.requests === 1 ? '' : 's'},{' '}
              {d.usage.inputTokens.toLocaleString('en-IN')} input tokens.
            </p>
          ) : null}
        </section>
      </div>

      {editable || d.status === 'failed' ? (
        <footer className="fixed inset-x-0 bottom-0 z-20 border-t bg-card/95 px-6 py-3 backdrop-blur md:left-[var(--sidebar-width,16rem)]">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setRejecting(true)}>
              Reject…
            </Button>
            <Button variant="outline" disabled={rerun.isPending} onClick={() => rerun.mutate()}>
              Re-run AI
            </Button>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {editable && low.length ? (
                <span className="text-sm text-destructive" data-testid="confirm-low">
                  Check the {low.length === 1 ? 'field' : `${low.length} fields`} marked Low first.
                </span>
              ) : null}
              {editable ? (
                <>
                  <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
                    Save for later
                  </Button>
                  <Button
                    disabled={!canApprove || approve.isPending}
                    onClick={() => approve.mutate()}
                  >
                    {approve.isPending ? 'Creating…' : 'Approve and create'}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        </footer>
      ) : null}
      <ReasonDialog
        open={rejecting}
        title={`Reject ${d.fileName}?`}
        description="Nothing is recorded. You can bring it back to review later."
        confirmLabel="Reject"
        destructive
        pending={reject.isPending}
        error={reject.error ? errorText(reject.error) : null}
        onConfirm={(reason) => reject.mutate(reason)}
        onClose={() => setRejecting(false)}
      />
    </div>
  );
}

/** The request body from the form (amounts as plain decimals). */
function body(f: ReviewForm) {
  return {
    recordAs: f.recordAs,
    vendorId: f.vendorId,
    newVendor: f.vendorId ? null : f.newVendor,
    issueDate: f.issueDate,
    dueDate: f.recordAs === 'bill' ? f.dueDate || null : null,
    billNo: f.billNo,
    placeOfSupply: f.placeOfSupply,
    currency: f.currency,
    amountsAre: f.amountsAre,
    paymentAccountId: f.recordAs === 'bill' ? null : f.paymentAccountId,
    memo: f.memo,
    lines: f.lines.map((l) => ({
      description: l.description,
      amount: l.amount || '0',
      accountId: l.accountId,
      taxCodeId: f.recordAs === 'journal_entry' ? null : l.taxCodeId,
    })),
  };
}

function Labelled({
  label,
  chip,
  children,
}: {
  label: string;
  chip?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Field label={label}>
      {() => (
        <div className="space-y-1">
          {children}
          {chip ? <div>{chip}</div> : null}
        </div>
      )}
    </Field>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-4', strong && 'border-t pt-1 font-semibold')}>
      <span>{label}</span>
      <span className="tnum text-right">{value}</span>
    </div>
  );
}

/** Read-only Dr/Cr voucher the approval will post (including input GST and A/P or bank). */
function VoucherPreview({
  voucher,
  pending,
  error,
}: {
  voucher: Voucher | null;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="rounded-lg border bg-card p-4" data-testid="voucher">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Journal entry it will post</h2>
        {pending ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" aria-label="Updating" />
        ) : null}
      </div>
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {!voucher ? (
        <p className="text-sm text-muted-foreground">Fill in the lines to see the entry.</p>
      ) : voucher.problem ? (
        <p className="text-sm text-destructive">
          {voucher.problem.charAt(0).toUpperCase() + voucher.problem.slice(1)}.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Account</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Debits</TableHead>
              <TableHead className="text-right">Credits</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {voucher.lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell>
                  {l.accountCode} {l.accountName}
                </TableCell>
                <TableCell className="text-muted-foreground">{l.description}</TableCell>
                <TableCell className="tnum text-right">
                  {l.debitMinor !== '0' ? formatMinor(BigInt(l.debitMinor)) : ''}
                </TableCell>
                <TableCell className="tnum text-right">
                  {l.creditMinor !== '0' ? formatMinor(BigInt(l.creditMinor)) : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2}>
                {voucher.balanced ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <CheckCircle2 className="size-4" aria-hidden /> Balanced
                  </span>
                ) : (
                  <span className="text-destructive">Out of balance</span>
                )}
              </TableCell>
              <TableCell className="tnum text-right font-semibold">
                {voucher.currency} {formatMinor(BigInt(voucher.totalDebitMinor))}
              </TableCell>
              <TableCell className="tnum text-right font-semibold">
                {voucher.currency} {formatMinor(BigInt(voucher.totalCreditMinor))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </div>
  );
}
